import { useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  FileText,
  Loader2,
  CheckCircle2,
  AlertOctagon,
  Activity,
  Users,
  TrendingUp,
  ArrowUpRight,
} from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";
import { useExtractionLive } from "../../../hooks/useExtractionLive";
import {
  formatStatusLabel,
  statusTone,
  type DocumentRow,
} from "../../../types/documents";

// ----------------------------------------------------------------- StatCard

interface StatCardProps {
  label: string;
  value: string | number;
  hint?: string;
  Icon: typeof FileText;
  tone: "blue" | "emerald" | "amber" | "rose";
  derived?: boolean;
}

const TONE: Record<StatCardProps["tone"], { chip: string }> = {
  blue:    { chip: "bg-blue-50 text-blue-600" },
  emerald: { chip: "bg-emerald-50 text-emerald-600" },
  amber:   { chip: "bg-amber-50 text-amber-600" },
  rose:    { chip: "bg-rose-50 text-rose-600" },
};

function StatCard({ label, value, hint, Icon, tone, derived }: StatCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-shadow hover:shadow-sm">
      <div className="flex items-start justify-between">
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${TONE[tone].chip}`}>
          <Icon className="h-4 w-4" />
        </span>
        {derived ? (
          <span
            title="Calculated from the latest documents -- the dashboard endpoint hasn't reported this metric yet."
            className="rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400"
          >
            sampled
          </span>
        ) : null}
      </div>
      <div className="mt-4 text-3xl font-semibold leading-none tabular-nums text-gray-900">
        {value}
      </div>
      <div className="mt-2 text-sm font-medium text-gray-600">{label}</div>
      {hint ? (
        <div className="mt-1 text-xs text-gray-400 line-clamp-1">{hint}</div>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------- helpers

function pickNumber(obj: unknown, ...keys: string[]): number | null {
  if (!obj || typeof obj !== "object") return null;
  const rec = obj as Record<string, unknown>;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "number") return v;
  }
  return null;
}

function pickByStatus(obj: unknown): Record<string, number> | null {
  if (!obj || typeof obj !== "object") return null;
  const rec = obj as Record<string, unknown>;
  const candidate = rec.byStatus ?? rec.by_status;
  if (!candidate || typeof candidate !== "object") return null;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(candidate as Record<string, unknown>)) {
    if (typeof v === "number") out[k] = v;
  }
  return Object.keys(out).length > 0 ? out : null;
}

type Tone = ReturnType<typeof statusTone>;
const TONE_ORDER: Tone[] = ["ok", "running", "warn", "fail", "neutral"];
const TONE_BG: Record<Tone, string> = {
  ok: "bg-emerald-500",
  running: "bg-blue-500",
  warn: "bg-amber-500",
  fail: "bg-rose-500",
  neutral: "bg-gray-300",
};
const TONE_LABEL: Record<Tone, string> = {
  ok: "Verified / output ready",
  running: "In flight",
  warn: "Rejected",
  fail: "Failed",
  neutral: "Other",
};

function bucketRowsByTone(rows: DocumentRow[]): Record<Tone, number> {
  const out: Record<Tone, number> = { ok: 0, running: 0, warn: 0, fail: 0, neutral: 0 };
  for (const r of rows) out[statusTone(r.status)]++;
  return out;
}

// ----------------------------------------------------------------- main

export function DashboardSummary() {
  const { data, isLoading } = useQuery({
    queryKey: ["ocr", "dashboard"],
    queryFn: () => ocrApi.dashboardInformation(),
    refetchInterval: 30_000,
    staleTime: 15_000,
    retry: 1,
  });

  const trackingSample = useQuery({
    queryKey: ["ocr", "tracking", { pageNumber: 1, recordsCount: 100 }],
    queryFn: () => ocrApi.trackingPage({ pageNumber: 1, recordsCount: 100 }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });

  const { events, connected: liveConnected } = useExtractionLive();

  const apiTotal      = pickNumber(data, "total", "total_records", "totalRecords");
  const apiInProgress = pickNumber(data, "in_progress", "inProgress", "running");
  const apiCompleted  = pickNumber(data, "completed");
  const apiFailed     = pickNumber(data, "failed", "errors");

  const sampleRows = trackingSample.data?.rows ?? [];

  const breakdown = useMemo(() => {
    const fromApi = pickByStatus(data);
    if (fromApi) {
      const buckets: Record<Tone, number> = { ok: 0, running: 0, warn: 0, fail: 0, neutral: 0 };
      for (const [status, count] of Object.entries(fromApi)) {
        buckets[statusTone(status)] += count;
      }
      const totalCount = Object.values(buckets).reduce((a, b) => a + b, 0);
      return { buckets, totalCount, source: "api" as const, fromApi };
    }
    const buckets = bucketRowsByTone(sampleRows);
    return { buckets, totalCount: sampleRows.length, source: "sample" as const, fromApi: null };
  }, [data, sampleRows]);

  const sampleTotal = trackingSample.data?.totalRecords ?? sampleRows.length;
  const totalValue      = apiTotal      ?? sampleTotal;
  const inProgressValue = apiInProgress ?? breakdown.buckets.running;
  const completedValue  = apiCompleted  ?? breakdown.buckets.ok;
  const failedValue     = apiFailed     ?? breakdown.buckets.fail + breakdown.buckets.warn;

  const topCustomers = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of sampleRows) {
      const name = (r.customer_name || "").trim();
      if (!name) continue;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [sampleRows]);

  const throughputPerMin = useMemo(() => {
    const cutoff = Date.now() - 60_000;
    let n = 0;
    for (const evt of events) {
      const t = evt.at ? Date.parse(evt.at) : NaN;
      if (Number.isFinite(t) && t >= cutoff) n++;
    }
    return n;
  }, [events]);

  const failureRatePct =
    totalValue > 0 ? Math.round((failedValue / totalValue) * 1000) / 10 : null;
  const completionRatePct =
    totalValue > 0 ? Math.round((completedValue / totalValue) * 1000) / 10 : null;

  const dashboardLoading = isLoading && !trackingSample.data;
  const fmt = (n: number): string => (dashboardLoading ? "…" : n.toLocaleString());

  return (
    <div className="space-y-4">
      {/* Headline counts ----------------------------------------------- */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total documents"
          value={fmt(totalValue)}
          hint={completionRatePct != null ? `${completionRatePct}% completed end-to-end` : undefined}
          Icon={FileText}
          tone="blue"
          derived={apiTotal == null}
        />
        <StatCard
          label="In progress"
          value={fmt(inProgressValue)}
          hint={liveConnected ? `${throughputPerMin} live event${throughputPerMin === 1 ? "" : "s"} in last 60s` : "ws-server offline"}
          Icon={Loader2}
          tone="amber"
          derived={apiInProgress == null}
        />
        <StatCard
          label="Completed"
          value={fmt(completedValue)}
          hint={completionRatePct != null ? `${completionRatePct}% of pipeline output` : undefined}
          Icon={CheckCircle2}
          tone="emerald"
          derived={apiCompleted == null}
        />
        <StatCard
          label="Failed"
          value={fmt(failedValue)}
          hint={failureRatePct != null ? `${failureRatePct}% failure rate` : undefined}
          Icon={AlertOctagon}
          tone="rose"
          derived={apiFailed == null}
        />
      </div>

      {/* Breakdown + leaderboard --------------------------------------- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-900">Status breakdown</h3>
            </div>
            <span className="text-xs text-gray-500">
              {breakdown.source === "api"
                ? "across all documents"
                : `from latest ${breakdown.totalCount} docs`}
            </span>
          </div>

          {breakdown.totalCount === 0 ? (
            <div className="py-8 text-center text-xs text-gray-500">
              No documents yet — drop a file to get started.
            </div>
          ) : (
            <>
              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
                {TONE_ORDER.map((t) => {
                  const n = breakdown.buckets[t];
                  if (!n) return null;
                  const pct = (n / breakdown.totalCount) * 100;
                  return (
                    <div
                      key={t}
                      className={`${TONE_BG[t]} transition-all`}
                      style={{ width: `${pct}%` }}
                      title={`${TONE_LABEL[t]}: ${n} (${pct.toFixed(1)}%)`}
                    />
                  );
                })}
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-xs sm:grid-cols-3">
                {TONE_ORDER.map((t) => {
                  const n = breakdown.buckets[t];
                  if (!n) return null;
                  const pct = ((n / breakdown.totalCount) * 100).toFixed(0);
                  return (
                    <li key={t} className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${TONE_BG[t]}`}
                      />
                      <span className="truncate text-gray-700">
                        {TONE_LABEL[t]}
                      </span>
                      <span className="ml-auto flex items-baseline gap-1 tabular-nums text-gray-500">
                        <span className="font-medium text-gray-900">{n}</span>
                        <span className="text-[10px] text-gray-400">{pct}%</span>
                      </span>
                    </li>
                  );
                })}
              </ul>

              {breakdown.source === "api" && breakdown.fromApi ? (
                <div className="mt-4 border-t border-gray-100 pt-3 text-xs">
                  <div className="mb-2 font-medium text-gray-500">Top stages</div>
                  <ul className="space-y-1.5">
                    {Object.entries(breakdown.fromApi)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 4)
                      .map(([status, count]) => (
                        <li
                          key={status}
                          className="flex items-center justify-between text-gray-700"
                        >
                          <span className="truncate">{formatStatusLabel(status)}</span>
                          <span className="tabular-nums text-gray-500">{count}</span>
                        </li>
                      ))}
                  </ul>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-900">Top customers</h3>
            </div>
            <span
              className="flex items-center gap-1 text-xs text-gray-500"
              title="From the latest 100 documents"
            >
              <TrendingUp className="h-3 w-3" />
              recent
            </span>
          </div>

          {topCustomers.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-500">No customer attribution yet.</div>
          ) : (
            <ul className="space-y-3">
              {topCustomers.map(([name, count], i) => {
                const pct = sampleRows.length > 0 ? (count / sampleRows.length) * 100 : 0;
                return (
                  <li key={name} className="text-xs">
                    <div className="mb-1 flex items-baseline justify-between gap-2">
                      <span className="flex items-center gap-2 truncate font-medium text-gray-700">
                        <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-[10px] font-semibold text-blue-700">
                          {i + 1}
                        </span>
                        <span className="truncate">{name}</span>
                      </span>
                      <span className="flex items-center gap-1.5 flex-shrink-0 tabular-nums">
                        <span className="font-semibold text-gray-900">{count}</span>
                        <ArrowUpRight className="h-3 w-3 text-gray-400" />
                      </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
